import classNames from "classnames";
import { useState } from "react";
import HandleEvents from "Util/HandleEvents";
import TablePagination from "Views/Components/Pagination/TablePagination";
import { DirectorBoardSection } from "./DirectorBoardSection";
import { DirectorInformationSection } from "./DirectorInformationSection";
import { DirectorDto } from "./DirectorSectionTypes";
import { SERVER_URL } from "Constants";
import axios, { AxiosError } from "axios";
import alertToast from "Util/ToastifyUtils";
import useAsync from "Hooks/useAsync";
import InlineSpinner from "Views/Components/Spinner/InlineSpinner";
import { PaginatedResponse } from "Types/PaginatedResponse";

const defaultResponse: PaginatedResponse<DirectorDto> = {
	data: [],
	pageNo: 1,
	pageSize: 3,
	totalCount: 0,
	totalPages: 0
}

const Default_PAGE_SIZE = 3;

const fetchDirectorDetails = async (monitoredId: string, pageNo: number = 1) => {
	try {
		const response = await axios.get<PaginatedResponse<DirectorDto>>(`${SERVER_URL}/api/directors/from-monitored/${monitoredId}`, {
			params: {
				pageNo,
				pageSize: Default_PAGE_SIZE
			}
		});
		return response.data;
	} catch (error) {
		if (axios.isAxiosError(error)) {
			const axiosError = error as AxiosError;
			alertToast(axiosError.response?.data?.message || axiosError.message, "error");
			return defaultResponse;
		}

		alertToast("Error fetching director details", "error");
		console.error(error);
		return defaultResponse;
	}
}


export const DirectorDetailSection = ({ monitoredId }: { monitoredId: string }) => {
	const [pageNo, setPageNo] = useState(0);
	const [showDirectorDetail, setShowDirectorDetail] = useState<Record<string, boolean>>({});

	const response = useAsync(async () => {
		const result = await fetchDirectorDetails(monitoredId, pageNo + 1);
		return result;
	}, [monitoredId, pageNo]);

	if (response.type === "loading") {
		return <InlineSpinner />;
	}

	if (!!!response.data) {
		return null;
	}

	const directorDetails = response.data;

	const renderTitleHeader = (detail: DirectorDto) => {
		if(!!detail.firstName) {
			return [detail.firstName, detail.otherName, detail.surname].filter(Boolean).join(" ");
		}

		if (!!detail.companyName) {
			return [detail.companyName, detail.companyAcn].filter(Boolean).join(" ");
		}

		return "Unknown";
	}

	return (
		<>
			{directorDetails.data.length > 0 && (
				<div className="accordion-group">
					{directorDetails.data.map(directorDetail => (
						<div className="accordion-item" key={directorDetail.id}>
							<div className="accordion-title-container">
								<h6 className="accordion-title-header director-name">{renderTitleHeader(directorDetail)}</h6>
								<span
									className={classNames('accordion-title-info accordion-icon icon-only icon-chevron-down', {
										open: showDirectorDetail[directorDetail.id],
									})}
									{...HandleEvents(() => setShowDirectorDetail(prev => ({
										...prev,
										[directorDetail.id]: !prev[directorDetail.id]
									})))}
								/>
							</div>
							<div 
								className={classNames("accordion-content", {
									expanded: showDirectorDetail[directorDetail.id],
									collapsed: !showDirectorDetail[directorDetail.id]
								})}
							>
								{showDirectorDetail[directorDetail.id] && (
									<>
										<DirectorInformationSection director={directorDetail} />
										<DirectorBoardSection director={directorDetail}/>
									</>
								)}
							</div>
						</div>
					))}
				</div>
			)}
			{directorDetails.totalCount > Default_PAGE_SIZE && (
				<TablePagination
					pageNo={pageNo}
					totalRecords={directorDetails.totalCount}
					perPage={Default_PAGE_SIZE}
					onPageChange={setPageNo}
				/>
			)}
			
		</>
	)
}